import { useEffect, useRef } from "react";
import { t } from "../lib/i18n.ts";
import { elementLabel, moveKeyframe, resolvedValue, setKeyframe, updateElement, updateScene } from "../lib/ops.ts";
import { trimEnd, trimStart, updateClip } from "../lib/audio.ts";
import { moveCue, updateCue } from "../lib/subtitles.ts";
import { Waveform, useAudio } from "./audio.tsx";
import type { EditorStore } from "../lib/store.ts";
import { useStore } from "./hooks.ts";

const LABEL_W = 196;
const fmt = (ms: number) => { const s = Math.floor(ms / 1000), f = Math.floor(ms % 1000); return `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}.${String(f).padStart(3, "0")}`; };

export function Timeline({ store }: { store: EditorStore }) {
  const st = useStore(store), p = st.project!, pps = st.pxPerSec / 1000;
  const areaW = Math.ceil(Math.max(p.duration_ms, 1000) * pps) + 60;
  const A = useAudio();
  const drag = useRef(null as null | ((x: number) => void));
  const frameMs = 1000 / p.fps;
  const scrollRef = useRef(null as HTMLDivElement | null);
  // long videos: while playing, keep the playhead in view; "fit" shows the whole project at once
  useEffect(() => {
    const el = scrollRef.current; if (!el || !st.playing) return;
    const x = LABEL_W + st.timeMs * pps;
    if (x < el.scrollLeft + LABEL_W || x > el.scrollLeft + el.clientWidth - 40) el.scrollLeft = Math.max(0, x - LABEL_W - 80);
  }, [st.timeMs, st.playing, pps]);
  const fit = () => { const el = scrollRef.current; if (el) store.setZoom(((el.clientWidth - LABEL_W - 110) / Math.max(1, p.duration_ms)) * 1000); if (el) el.scrollLeft = 0; };

  const step = [100, 250, 500, 1000, 2000, 5000, 10000, 30000].find((s) => s * pps >= 70) ?? 60000;
  const ticks: number[] = []; for (let m = 0; m <= p.duration_ms + step; m += step) ticks.push(m);

  const beginDrag = (e: any, fn: (clientX: number) => void) => {
    e.stopPropagation(); e.currentTarget.setPointerCapture?.(e.pointerId);
    drag.current = fn; fn(e.clientX);
  };
  const areaRef = useRef(null as HTMLDivElement | null);
  const xToMs = (clientX: number) => (clientX - areaRef.current!.getBoundingClientRect().left) / pps;
  const seekAt = (clientX: number) => store.seek(xToMs(clientX));
  const move = (e: any) => drag.current?.(e.clientX);
  const up = () => { drag.current = null; };

  const scenes = [...p.scenes].sort((a, b) => a.start_ms - b.start_ms);
  const rows: any[] = [];
  for (const sc of scenes) for (const id of sc.element_ids) {
    const el = p.elements[id]; if (!el) continue;
    const trs = p.tracks.filter((x) => x.element_id === id);
    rows.push(
      <div className="tl-r" key={id}>
        <div className="tl-label" role="button" tabIndex={0} aria-selected={st.selection.kind === "element" && st.selection.id === id}
          onClick={() => store.select({ kind: "element", id })} onKeyDown={(e: any) => { if (e.key === "Enter") store.select({ kind: "element", id }); }}>
          <span aria-hidden="true">{el.type === "text" ? "T" : el.type === "shape" ? "▭" : el.type === "image" ? "▣" : el.type === "video" ? "▶" : el.type === "mesh" ? "◈" : el.type === "html" ? "‹›" : "◉"}</span>{elementLabel(el)}</div>
        <div className="tl-area tl-track" style={{ width: areaW }}>
          {el.type === "video" && (() => {
            const abs = sc.start_ms + el.start_ms, width = Math.max(8, el.duration_ms * pps);
            const src = A.list?.assets.find((a) => a.id === el.asset_id)?.duration_ms ?? el.in_ms + el.duration_ms * el.speed;
            return <div className="vclip" role="button" tabIndex={0} aria-selected={st.selection.kind === "element" && st.selection.id === el.id}
              aria-label={`${el.name} · ${fmt(abs)} · ${fmt(el.duration_ms)}`} style={{ left: abs * pps, width }}
              onKeyDown={(e: any) => { if (e.key === "Enter") store.select({ kind: "element", id: el.id }); }}
              onPointerDown={(e: any) => { store.select({ kind: "element", id: el.id }); const off = xToMs(e.clientX) - abs;
                beginDrag(e, (cx) => store.edit((q) => updateElement(q, el.id, { start_ms: Math.round(Math.min(sc.duration_ms - el.duration_ms, Math.max(0, xToMs(cx) - off - sc.start_ms))) }), `vcm:${el.id}`)); }}
              onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
              <span>{el.name}</span>
              <i className="rz l" onPointerDown={(e: any) => { e.stopPropagation(); const end = abs + el.duration_ms;
                beginDrag(e, (cx) => store.edit((q) => { const min = Math.max(sc.start_ms, abs - el.in_ms / el.speed); const start = Math.min(end - 100, Math.max(min, xToMs(cx)));
                  const delta = Math.round(start - abs); return updateElement(q, el.id, { start_ms: el.start_ms + delta, duration_ms: el.duration_ms - delta, in_ms: Math.max(0, el.in_ms + Math.round(delta * el.speed)) }); }, `vct:${el.id}`)); }}
                onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
              <i className="rz r" onPointerDown={(e: any) => { e.stopPropagation();
                beginDrag(e, (cx) => store.edit((q) => { const end = Math.min(sc.start_ms + sc.duration_ms, abs + Math.max(100, (src - el.in_ms) / el.speed));
                  return updateElement(q, el.id, { duration_ms: Math.max(100, Math.round(Math.min(end, xToMs(cx)) - abs)) }); }, `vct:${el.id}`)); }}
                onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
            </div>;
          })()}
        </div>
      </div>);
    for (const tr of trs) {
      rows.push(
        <div className="tl-r" key={tr.id}>
          <div className="tl-label prop">{tr.property}</div>
          <div className="tl-area tl-track" style={{ width: areaW }} data-track={tr.id}
            onDoubleClick={(e: any) => {
              const ms = Math.round(Math.min(Math.max(sc.start_ms, sc.start_ms + (e.clientX - e.currentTarget.getBoundingClientRect().left) / pps - sc.start_ms + 0), sc.start_ms + sc.duration_ms));
              const abs = Math.round((e.clientX - e.currentTarget.getBoundingClientRect().left) / pps);
              const t0 = Math.min(Math.max(abs, sc.start_ms), sc.start_ms + sc.duration_ms); void ms;
              store.edit((q) => setKeyframe(q, id, tr.property, t0, resolvedValue(q, id, tr.property, t0)));
            }}>
            {tr.keyframes.map((kf) => {
              const abs = sc.start_ms + kf.time_ms, on = st.selection.kind === "keyframe" && st.selection.kfId === kf.id;
              return <button key={kf.id} className="kf" style={{ left: abs * pps }} aria-selected={on} aria-label={`${tr.property} ${kf.time_ms} ms`}
                onPointerDown={(e: any) => { store.select({ kind: "keyframe", trackId: tr.id, kfId: kf.id, elementId: id }); beginDrag(e, (cx) => store.edit((q) => moveKeyframe(q, tr.id, kf.id, xToMs(cx) - sc.start_ms), `kfm:${kf.id}`)); }}
                onPointerMove={move} onPointerUp={up} onPointerCancel={up} onDoubleClick={(e: any) => e.stopPropagation()} />;
            })}
          </div>
        </div>);
    }
  }

  for (const c of p.audio) {
    const meta = A.meta(c.asset_id), on = st.selection.kind === "audio" && st.selection.id === c.id, src = meta?.duration_ms ?? c.in_ms + c.duration_ms;
    const w = Math.max(6, c.duration_ms * pps);
    rows.push(
      <div className="tl-r tall" key={c.id}>
        <div className="tl-label" role="button" tabIndex={0} aria-selected={on} onClick={() => store.select({ kind: "audio", id: c.id })}
          onKeyDown={(e: any) => { if (e.key === "Enter") store.select({ kind: "audio", id: c.id }); }}><span aria-hidden="true">♪</span>{c.name || meta?.name || t("au.lane")}</div>
        <div className="tl-area tl-track" style={{ width: areaW }}>
          <div className="aclip" data-muted={c.muted} aria-selected={on} role="button" tabIndex={0} aria-label={`${t("au.clip")} ${c.name || meta?.name || ""}`} style={{ left: c.start_ms * pps, width: w }}
            onKeyDown={(e: any) => { if (e.key === "Enter") store.select({ kind: "audio", id: c.id }); }}
            onPointerDown={(e: any) => { store.select({ kind: "audio", id: c.id }); const off = xToMs(e.clientX) - c.start_ms;
              beginDrag(e, (cx) => store.edit((q) => updateClip(q, c.id, { start_ms: Math.round(xToMs(cx) - off) }, src), `acm:${c.id}`)); }}
            onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
            <Waveform meta={meta} clip={c} width={w} height={36} />
            <span className="rz l" onPointerDown={(e: any) => { store.select({ kind: "audio", id: c.id }); beginDrag(e, (cx) => store.edit((q) => trimStart(q, c.id, xToMs(cx), src), `act:${c.id}`)); }}
              onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
            <span className="rz r" onPointerDown={(e: any) => { store.select({ kind: "audio", id: c.id }); beginDrag(e, (cx) => store.edit((q) => trimEnd(q, c.id, xToMs(cx), src), `act:${c.id}`)); }}
              onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
          </div>
        </div>
      </div>);
  }

  if (p.subtitles.cues.length) rows.push(
    <div className="tl-r" key="subs">
      <div className="tl-label"><span aria-hidden="true">Cc</span>{t("sub.lane")}</div>
      <div className="tl-area tl-track" style={{ width: areaW }}>
        {p.subtitles.cues.map((c) => {
          const on = st.selection.kind === "cue" && st.selection.id === c.id;
          return <div key={c.id} className="cue" role="button" tabIndex={0} aria-selected={on} aria-label={`${t("sub.cue")} ${c.text}`} title={c.text}
            style={{ left: c.start_ms * pps, width: Math.max(8, (c.end_ms - c.start_ms) * pps) }}
            onKeyDown={(e: any) => { if (e.key === "Enter") store.select({ kind: "cue", id: c.id }); }}
            onPointerDown={(e: any) => { store.select({ kind: "cue", id: c.id }); const off = xToMs(e.clientX) - c.start_ms;
              beginDrag(e, (cx) => store.edit((q) => moveCue(q, c.id, Math.round(xToMs(cx) - off)), `cm:${c.id}`)); }}
            onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
            <span className="cue-t">{c.text}</span>
            <span className="rz r" onPointerDown={(e: any) => { store.select({ kind: "cue", id: c.id }); beginDrag(e, (cx) => store.edit((q) => updateCue(q, c.id, { end_ms: Math.round(xToMs(cx)) }), `ce:${c.id}`)); }}
              onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
          </div>;
        })}
      </div>
    </div>);

  return (
    <section className="tl" aria-label="Timeline">
      <div className="tl-bar">
        <button className="btn sm icon" onClick={() => store.seek(0)} aria-label={t("tl.start")} title={t("tl.start")}>⏮</button>
        <button className="btn sm icon" onClick={() => store.seek(Math.max(0, st.timeMs - frameMs))} aria-label={t("tl.prev")} title={t("tl.prev")}>◀</button>
        <button className="btn sm primary icon" onClick={() => (st.playing ? store.pause() : store.play())} aria-label={st.playing ? t("tl.pause") : t("tl.play")} title={st.playing ? t("tl.pause") : t("tl.play")}>{st.playing ? "❚❚" : "▶"}</button>
        <button className="btn sm icon" onClick={() => store.seek(st.timeMs + frameMs)} aria-label={t("tl.next")} title={t("tl.next")}>▶|</button>
        <button className="btn sm icon" aria-pressed={A.muted} onClick={() => A.setMuted(!A.muted)} aria-label={A.muted ? t("au.unmute_preview") : t("au.mute_preview")} title={A.muted ? t("au.unmute_preview") : t("au.mute_preview")}>{A.muted ? "🔇" : "🔊"}</button>
        <span className="mono time" data-testid="time">{fmt(st.timeMs)} / {fmt(p.duration_ms)}</span>
        <span className="muted" style={{ fontSize: 12, flex: 1 }}>{t("tl.dbl")}</span>
        <button className="btn sm icon" onClick={() => store.setZoom(st.pxPerSec / 1.25)} aria-label={t("tl.zoom_out")} title={t("tl.zoom_out")}>−</button>
        <button className="btn sm" onClick={fit} title={t("tl.fit")}>{t("tl.fit")}</button>
        <button className="btn sm icon" onClick={() => store.setZoom(st.pxPerSec * 1.25)} aria-label={t("tl.zoom_in")} title={t("tl.zoom_in")}>+</button>
      </div>
      <div className="tl-scroll" ref={scrollRef} onPointerMove={move} onPointerUp={up}>
        <div className="tl-inner" style={{ width: LABEL_W + areaW }}>
          <div className="tl-r">
            <div className="tl-label" style={{ background: "var(--bg-subtle)", position: "sticky", top: 0, zIndex: 6 }}>{t("tl.time")}</div>
            <div className="tl-ruler tl-area" ref={areaRef} style={{ width: areaW }} onPointerDown={(e: any) => beginDrag(e, seekAt)} onPointerMove={move} onPointerUp={up}>
              {ticks.map((m) => <span key={m} style={{ left: m * pps }}><i style={{ left: -3, height: 6 }} />{fmt(m).slice(3, 8)}{step < 1000 ? "." + String(m % 1000).padStart(3, "0")[0] : ""}</span>)}
            </div>
          </div>
          <div className="tl-r">
            <div className="tl-label">{t("tl.scenes")}</div>
            <div className="tl-area tl-track" style={{ width: areaW, height: 26 }}>
              {scenes.map((sc, si) => (
                <div key={sc.id} className="tl-scene" role="button" tabIndex={0} aria-selected={st.selection.kind === "scene" && st.selection.id === sc.id}
                  style={{ left: sc.start_ms * pps, width: Math.max(8, sc.duration_ms * pps) }}
                  onKeyDown={(e: any) => { if (e.key === "Enter") store.select({ kind: "scene", id: sc.id }); }}
                  onPointerDown={(e: any) => { store.select({ kind: "scene", id: sc.id }); const off = xToMs(e.clientX) - sc.start_ms;
                    beginDrag(e, (cx) => store.edit((q) => updateScene(q, sc.id, { start_ms: Math.max(0, Math.round(xToMs(cx) - off)) }), `scm:${sc.id}`)); }}
                  onPointerMove={move} onPointerUp={up} onPointerCancel={up}>
                  {sc.name || `${t("insp.scene")} ${si + 1}`}
                  <span className="rz" onPointerDown={(e: any) => beginDrag(e, (cx) => store.edit((q) => updateScene(q, sc.id, { duration_ms: Math.max(100, Math.round(xToMs(cx) - sc.start_ms)) }), `scr:${sc.id}`))}
                    onPointerMove={move} onPointerUp={up} onPointerCancel={up} />
                </div>))}
            </div>
          </div>
          {rows.length || p.audio.length || p.subtitles.cues.length ? rows : <div className="tl-empty">{t("tl.empty")}</div>}
          <div className="playhead" style={{ left: LABEL_W + st.timeMs * pps }} data-testid="playhead" />
        </div>
      </div>
    </section>
  );
}
